import { useEffect, useState } from "react";
import { api, type Signal } from "../lib/api";
import { duration, money, odds, outcome, signClass, signedPct } from "../lib/format";

/**
 * Detail signálu s formulářem pro záznam sázky.
 *
 * Zapisuje se skutečně získaný kurz, ne ten z notifikace. Rozdíl obou je
 * jediný způsob, jak zjistit, jestli jsou prahy nastavené realisticky.
 */
export function SignalDetail({ id, onBack }: { id: string; onBack: () => void }) {
  const [data, setData] = useState<{ signal: Signal; history: { captured_at: string; price: string }[] } | null>(null);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState("");
  const [matchedOdds, setMatchedOdds] = useState("");
  const [stake, setStake] = useState("");
  const [note, setNote] = useState("");
  const [reason, setReason] = useState("");

  const load = () =>
    api.signal(id).then((r) => {
      setData(r);
      setMatchedOdds(r.signal.matched_odds ?? r.signal.offered_odds);
      setStake(r.signal.suggested_stake);
    }).catch((e: Error) => setError(e.message));

  useEffect(() => { void load(); }, [id]);

  if (error) return <p className="notice warn">{error}</p>;
  if (!data) return <p className="empty">Načítám.</p>;

  const { signal } = data;
  const fairOdds = 1 / Number(signal.fair_prob);

  const submitBet = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    try {
      await api.recordBet(id, {
        matchedOdds: Number(matchedOdds.replace(",", ".")),
        stake: Number(stake.replace(",", ".")),
        ...(note ? { note } : {}),
      });
      setSaved("Sázka zaznamenaná.");
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const submitSkip = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      await api.skip(id, reason);
      setSaved("Zaznamenáno jako nesázené.");
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <>
      <p style={{ marginTop: "1.5rem" }}>
        <button className="action quiet" onClick={onBack}>Zpět na signály</button>
      </p>

      <h1>{signal.home_team} vs {signal.away_team}</h1>
      <p className="lede">
        {signal.league_name}, výkop za {duration(new Date(signal.starts_at).getTime() - Date.now())}.
        {" "}{signal.source_name}, {outcome(signal.outcome_code)}, trh {signal.market_key}.
      </p>

      <dl className="readout">
        <div>
          <dt>Nabídka</dt>
          <dd className="num">{odds(signal.offered_odds)}</dd>
        </div>
        <div>
          <dt>Férová cena</dt>
          <dd className="num">{odds(fairOdds)}</dd>
        </div>
        <div>
          <dt>Edge</dt>
          <dd className={`num ${signClass(Number(signal.edge))}`}>{signedPct(Number(signal.edge))}</dd>
        </div>
        {signal.clv_signal !== null && (
          <div>
            <dt>CLV signálu</dt>
            <dd className={`num ${signClass(Number(signal.clv_signal))}`}>
              {signedPct(Number(signal.clv_signal))}
            </dd>
          </div>
        )}
      </dl>

      {signal.mode === "paper" ? (
        <p className="notice">
          Papírový signál. Kniha {signal.source_name} není označená jako sázková, takže se
          nezaznamenává plnění. Doplň ji do <code>BETTABLE_SOURCES</code> a spusť seed, pokud u ní
          účet máš.
        </p>
      ) : (
        <>
          <h2>Záznam sázky</h2>
          <p className="lede">
            Zapiš kurz, který jsi opravdu dostal, ne ten z notifikace. Rozdíl obou je jediný
            způsob, jak zjistit, jestli jsou prahy nastavené realisticky.
          </p>
          <form onSubmit={submitBet}>
            <div className="field-row">
              <div className="field">
                <label htmlFor="matched">Získaný kurz</label>
                <input id="matched" inputMode="decimal" value={matchedOdds}
                       onChange={(e) => setMatchedOdds(e.target.value)} required />
              </div>
              <div className="field">
                <label htmlFor="stake">Vklad v Kč</label>
                <input id="stake" inputMode="decimal" value={stake}
                       onChange={(e) => setStake(e.target.value)} required />
              </div>
              <div className="field" style={{ flexBasis: "16rem" }}>
                <label htmlFor="note">Poznámka</label>
                <input id="note" value={note} onChange={(e) => setNote(e.target.value)} />
              </div>
              <button className="action" type="submit">Uložit sázku</button>
            </div>
          </form>

          <h2>Nesázeno</h2>
          <form onSubmit={submitSkip}>
            <div className="field-row">
              <div className="field" style={{ flexBasis: "20rem" }}>
                <label htmlFor="reason">Důvod</label>
                <input id="reason" value={reason} onChange={(e) => setReason(e.target.value)}
                       placeholder="kurz už zmizel" required />
              </div>
              <button className="action quiet" type="submit">Zaznamenat důvod</button>
            </div>
          </form>
        </>
      )}

      {saved && <p className="notice">{saved}</p>}
      {signal.matched_odds && (
        <p className="notice">
          Uložený kurz {odds(signal.matched_odds)}, navržená sázka byla {money(signal.suggested_stake)}.
        </p>
      )}

      <h2>Pohyb kurzu</h2>
      {data.history.length === 0 ? (
        <p className="empty">Zatím jen jeden odečet.</p>
      ) : (
        <div className="scroll-x">
          <table>
            <thead>
              <tr><th>Čas odečtu</th><th className="right">Kurz</th></tr>
            </thead>
            <tbody>
              {data.history.slice(-40).map((row, index) => (
                <tr key={index}>
                  <td>{new Date(row.captured_at).toLocaleString("cs-CZ")}</td>
                  <td className="right num">{odds(row.price)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
